import React, { useEffect, useMemo, useState } from 'react';
import { CircleMarker, MapContainer, Polyline, TileLayer } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { MapPinOff } from 'lucide-react';
import { CarSpinner } from '../common/CarSpinner';
import { TILE_PROVIDERS } from '../../config/tileProviders';
import config, { ENDPOINTS } from '../../api/config';
import { getAuthToken } from '../../utils/storageKeys';
import { cn } from '../../lib/utils';

// Route line matches the iOS spec: 3.5px accent over a 6px black understroke.
const LINE = { color: '#3B82F6', weight: 3.5, lineJoin: 'round', lineCap: 'round' };
const UNDERSTROKE = { color: '#000000', opacity: 0.55, weight: 6, lineJoin: 'round', lineCap: 'round' };

/** Static (non-interactive) OSM route map — darkened tiles, accent polyline. */
export function RouteMap({ route, className }) {
  // Stabilize bounds on coordinate values — parent often passes a new route[]
  // reference each render; depending on the array identity remounts Leaflet.
  const bounds = useMemo(() => {
    let minLat = Infinity, maxLat = -Infinity, minLon = Infinity, maxLon = -Infinity;
    route.forEach(([lat, lon]) => {
      minLat = Math.min(minLat, lat); maxLat = Math.max(maxLat, lat);
      minLon = Math.min(minLon, lon); maxLon = Math.max(maxLon, lon);
    });
    if (maxLat - minLat < 1e-4) { minLat -= 5e-4; maxLat += 5e-4; }
    if (maxLon - minLon < 1e-4) { minLon -= 5e-4; maxLon += 5e-4; }
    return [[minLat, minLon], [maxLat, maxLon]];
    // eslint-disable-next-line react-hooks/exhaustive-deps -- compare by value, not reference
  }, [route.length, route[0]?.[0], route[0]?.[1], route[route.length - 1]?.[0], route[route.length - 1]?.[1]]);

  const osm = TILE_PROVIDERS.openStreetMap;
  return (
    <div
      className={cn(
        // `isolate` traps Leaflet's internal z-indexes (up to 700) inside the card
        // so maps can never paint over modals/overlays.
        'relative isolate overflow-hidden bg-lap-card pointer-events-none',
        // Darken the raster tiles (iOS uses a black@0.45 scrim over the snapshot)
        '[&_.leaflet-tile-pane]:brightness-[0.55] [&_.leaflet-tile-pane]:saturate-[0.8]',
        className
      )}
      data-testid="route-map"
    >
      <MapContainer
        bounds={bounds}
        boundsOptions={{ padding: [24, 24] }}
        whenReady={(e) => {
          // Leaflet can measure mid-layout — one invalidate, no overlay flicker.
          const map = e.target;
          setTimeout(() => {
            map.invalidateSize();
            map.fitBounds(bounds, { padding: [24, 24] });
          }, 100);
        }}
        style={{ height: '100%', width: '100%', background: '#16181C' }}
        zoomControl={false}
        attributionControl={false}
        dragging={false}
        touchZoom={false}
        doubleClickZoom={false}
        scrollWheelZoom={false}
        boxZoom={false}
        keyboard={false}
      >
        <TileLayer url={osm.url} subdomains={osm.subdomains} maxZoom={osm.maxZoom} />
        <Polyline positions={route} pathOptions={UNDERSTROKE} />
        <Polyline positions={route} pathOptions={LINE} />
        <CircleMarker center={route[0]} radius={4} pathOptions={{ color: '#FFFFFF', fillColor: '#FFFFFF', fillOpacity: 1 }} />
        <CircleMarker center={route[route.length - 1]} radius={4} pathOptions={{ color: '#3B82F6', fillColor: '#3B82F6', fillOpacity: 1 }} />
      </MapContainer>
      <div className="absolute bottom-2 right-2 z-[700] px-2 py-0.5 rounded-full bg-black/55 text-[10px] text-white/70">
        © OpenStreetMap
      </div>
    </div>
  );
}

/** Fallback route preview for session posts without GPS route: themed track SVG. */
function SessionTrackSvg({ sessionId, className }) {
  const [svg, setSvg] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = getAuthToken();
        const url = `${config.apiUrl}${ENDPOINTS.sessionTrackSvg(sessionId)}`;
        const { fetchSvgCached } = await import('../../utils/svgCache');
        let content = await fetchSvgCached(url, {
          headers: { Authorization: `Bearer ${token}` },
        });
        content = content
          .replace(/fill="black"/gi, 'fill="transparent"')
          .replace(/fill="#000000"/gi, 'fill="transparent"')
          .replace(/fill="#000"/gi, 'fill="transparent"');
        if (!cancelled) setSvg(content);
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  if (failed) return <NoRoute className={className} />;
  if (!svg) {
    return (
      <div className={cn('bg-lap-card flex items-center justify-center', className)} data-testid="route-preview-loading">
        <CarSpinner className="h-16 w-16" />
      </div>
    );
  }
  return (
    <div
      className={cn('bg-lap-card flex items-center justify-center p-6 [&_svg]:max-w-full [&_svg]:max-h-full', className)}
      data-testid="route-preview"
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
}

function NoRoute({ className }) {
  return (
    <div className={cn('bg-lap-card flex flex-col items-center justify-center gap-1.5 text-lap-dim', className)}>
      <MapPinOff className="w-5 h-5" />
      <span className="text-xs font-medium">No route</span>
    </div>
  );
}

/** Picks the best map for a post: GPS route → session track SVG → "No route". */
export function PostMap({ post, className }) {
  if (post.route && post.route.length >= 2) {
    return <RouteMap route={post.route} className={className} />;
  }
  // /aim/session/{id}/track-svg is owner-scoped, so asking for someone else's
  // session just 404s after a round trip — every card in the feed paying for a
  // doomed request is why maps sat on a skeleton. Only ask when we may read it.
  const canReadSession =
    post.is_mine || post.session_access?.status === 'approved';
  if (post.session?.session_id && canReadSession) {
    return <SessionTrackSvg sessionId={post.session.session_id} className={className} />;
  }
  return <NoRoute className={className} />;
}
